// pages/Roles.jsx — Master Permission Matrix + Roles CRUD
import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePermission, Can } from '../lib/permissions';
import Portal from '../components/Portal';
import { Plus, X, Save, Trash2, ShieldCheck, Search, Check } from 'lucide-react';

export default function Roles() {
  const { has } = usePermission();
  const { refresh } = useAuth();
  const canCreate = has('roles.create');
  const canEditPerms = has('roles.edit_permissions');
  const canDelete = has('roles.delete');

  const [tab, setTab] = useState('matrix');
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // role object or 'new'
  const [dirty, setDirty] = useState({}); // roleId → Set(perm_keys)
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get('/roles-matrix/data');
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    if (!data) return {};
    const g = {};
    for (const p of data.permissions) (g[p.category] ||= []).push(p);
    return g;
  }, [data]);

  const rolePerms = (roleId) => {
    if (dirty[roleId]) return dirty[roleId];
    return new Set(data?.matrix?.[roleId] || []);
  };
  const toggle = (roleId, permKey) => {
    if (!canEditPerms) return;
    const cur = new Set(rolePerms(roleId));
    if (cur.has(permKey)) cur.delete(permKey); else cur.add(permKey);
    setDirty({ ...dirty, [roleId]: cur });
  };
  const saveRole = async (roleId) => {
    if (!dirty[roleId]) return;
    setBusy(true);
    try {
      await api.put(`/roles/${roleId}/permissions`, { permissions: [...dirty[roleId]] });
      const nd = { ...dirty }; delete nd[roleId]; setDirty(nd);
      await load();
      await refresh();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const saveAll = async () => {
    setBusy(true);
    try {
      for (const rid of Object.keys(dirty)) {
        await api.put(`/roles/${rid}/permissions`, { permissions: [...dirty[rid]] });
      }
      setDirty({});
      await load();
      await refresh();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const deleteRole = async (role) => {
    if (!confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/roles/${role.id}`);
      await load();
      await refresh();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  if (!data) return <div className="text-white p-8">Loading permission matrix…</div>;

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div style={{ padding: '24px 24px 80px', maxWidth: 1400, margin: '0 auto' }} data-testid="roles-page">
      <div className="flex items-center justify-between mb-4" style={{ color: 'var(--trello-text)' }}>
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={26} />
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Roles & Permissions</h1>
          </div>
          <p style={{ opacity: 0.85, marginTop: 4, fontSize: 13 }}>Every action is controlled by an atomic permission. Roles are just named collections of permissions.</p>
        </div>
        <div className="flex gap-2">
          {dirtyCount > 0 && (
            <button className="tm-btn" style={{ background: '#519839' }} onClick={saveAll} disabled={busy} data-testid="save-all-btn">
              <Save size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: -2 }} />
              Save all changes ({dirtyCount})
            </button>
          )}
          <Can perm="roles.create">
            <button className="tm-btn" onClick={() => setEditing('new')} data-testid="create-role-btn" style={{ padding: '8px 16px', borderRadius: 10 }}>
              <Plus size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />Create role
            </button>
          </Can>
        </div>
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        <button className={tab === 'matrix' ? 'tm-btn' : 'tm-btn-ghost'} onClick={() => setTab('matrix')} data-testid="tab-matrix" style={{ borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>Permission Matrix</button>
        <button className={tab === 'roles' ? 'tm-btn' : 'tm-btn-ghost'} onClick={() => setTab('roles')} data-testid="tab-roles" style={{ borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>Roles</button>
        <button className={tab === 'permissions' ? 'tm-btn' : 'tm-btn-ghost'} onClick={() => setTab('permissions')} data-testid="tab-permissions" style={{ borderRadius: 20, padding: '8px 16px', fontSize: 13, fontWeight: 600 }}>Permissions catalog</button>
      </div>

      {tab === 'matrix' && <MatrixGrid data={data} grouped={grouped} rolePerms={rolePerms} toggle={toggle} dirty={dirty} canEditPerms={canEditPerms} saveRole={saveRole} busy={busy} />}
      {tab === 'roles' && <RolesList roles={data.roles} onEdit={(r) => setEditing(r)} onDelete={deleteRole} canDelete={canDelete} />}
      {tab === 'permissions' && <PermissionsCatalog grouped={grouped} />}

      {editing && (
        <RoleModal
          role={editing === 'new' ? null : editing}
          grouped={grouped}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); await refresh(); }}
        />
      )}
    </div>
  );
}

// ============ Matrix Grid ============
function MatrixGrid({ data, grouped, rolePerms, toggle, dirty, canEditPerms, saveRole, busy }) {
  const [q, setQ] = useState('');
  const roles = data.roles;
  const categories = Object.keys(grouped);

  return (
    <div className="tm-stat-card" style={{ borderLeft: 'none', overflow: 'hidden', padding: 0 }} data-testid="matrix-grid">
      <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--trello-border)' }}>
        <Search size={14} />
        <input className="tm-input" placeholder="Filter permissions…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 320 }} data-testid="matrix-search" />
        <div style={{ flex: 1 }} />
        {Object.keys(dirty).length > 0 && (
          <span style={{ fontSize: 12, color: '#D29034', fontWeight: 700 }}>● Unsaved changes on {Object.keys(dirty).length} role(s)</span>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, background: 'var(--trello-list-bg)', zIndex: 2 }}>
            <tr>
              <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 700, minWidth: 260 }}>Permission</th>
              {roles.map((r) => (
                <th key={r.id} style={{ padding: '10px 8px', textAlign: 'center', minWidth: 100 }}>
                  <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <span className="tm-badge" style={{ background: r.color, color: '#fff', fontWeight: 700 }}>{r.name}</span>
                    {r.is_system ? <span style={{ fontSize: 10, color: 'var(--trello-text-muted)' }}>system</span> : null}
                    {dirty[r.id] && (
                      <button className="tm-btn" style={{ background: '#519839', padding: '2px 6px', fontSize: 10 }}
                        onClick={() => saveRole(r.id)} disabled={busy} data-testid={`save-${r.id}`}>
                        <Save size={10} style={{ display: 'inline', verticalAlign: -1, marginRight: 3 }} />Save
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => {
              const perms = grouped[cat].filter((p) =>
                !q.trim() || p.label.toLowerCase().includes(q.toLowerCase()) || p.perm_key.toLowerCase().includes(q.toLowerCase()));
              if (!perms.length) return null;
              return (
                <React.Fragment key={cat}>
                  <tr style={{ background: 'var(--trello-list-bg)' }}>
                    <td colSpan={roles.length + 1} style={{ padding: '6px 12px', fontWeight: 700, fontSize: 12, color: 'var(--trello-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{cat}</td>
                  </tr>
                  {perms.map((p) => (
                    <tr key={p.perm_key} style={{ borderTop: '1px solid var(--trello-border)' }} data-testid={`row-${p.perm_key}`}>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ fontWeight: 600 }}>{p.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', fontFamily: 'monospace' }}>{p.perm_key}</div>
                      </td>
                      {roles.map((r) => {
                        const on = rolePerms(r.id).has(p.perm_key);
                        return (
                          <td key={r.id} style={{ padding: '4px', textAlign: 'center' }}>
                            <button
                              onClick={() => toggle(r.id, p.perm_key)}
                              disabled={!canEditPerms}
                              data-testid={`cell-${r.id}-${p.perm_key}`}
                              style={{
                                width: 26, height: 26, borderRadius: 4,
                                border: on ? `2px solid ${r.color}` : '2px solid #DFE1E6',
                                background: on ? r.color : '#fff',
                                cursor: canEditPerms ? 'pointer' : 'not-allowed',
                                color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                              {on && <Check size={14} strokeWidth={3} />}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ Roles list ============
function RolesList({ roles, onEdit, onDelete, canDelete }) {
  return (
    <div className="tm-stat-card" style={{ borderLeft: 'none' }} data-testid="roles-list">
      {roles.map((r) => (
        <div key={r.id} className="flex items-center gap-3 p-3 rounded" style={{ borderBottom: '1px solid var(--trello-border)' }} data-testid={`role-row-${r.id}`}>
          <div className="tm-badge" style={{ background: r.color, color: '#fff', fontWeight: 700, minWidth: 90, textAlign: 'center' }}>{r.name}</div>
          {r.is_system ? <span className="tm-pill" style={{ background: 'var(--trello-list-bg)', color: '#5243AA', fontSize: 10 }}>system</span> : null}
          <span className="tm-pill" style={{
            background: r.is_active ? 'rgba(81, 152, 57, 0.15)' : 'rgba(127, 127, 127, 0.15)',
            color: r.is_active ? '#385723' : '#7F7F7F',
            fontSize: 10,
            fontWeight: 700
          }}>
            {r.is_active ? 'active' : 'inactive'}
          </span>
          <div style={{ flex: 1, fontSize: 12, color: 'var(--trello-text-muted)' }}>
            {r.system_key === 'leader' && 'Full workspace administrator'}
            {r.system_key === 'manager' && 'Manages projects and team members'}
            {r.system_key === 'team' && 'Contributes to assigned tasks'}
            {!r.system_key && (r.description || '')}
          </div>
          <button className="tm-btn-ghost" onClick={() => onEdit(r)} data-testid={`edit-role-${r.id}`}>Edit</button>
          {canDelete && !r.is_system && (
            <button className="tm-btn-ghost" onClick={() => onDelete(r)} data-testid={`delete-role-${r.id}`} style={{ color: '#DE350B' }}>
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ============ Permissions catalog (read-only) ============
function PermissionsCatalog({ grouped }) {
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="perms-catalog">
      {Object.entries(grouped).map(([cat, perms]) => (
        <div key={cat} className="tm-stat-card" style={{ borderLeft: 'none' }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--trello-text-muted)' }}>{cat}</h4>
          {perms.map((p) => (
            <div key={p.perm_key} style={{ padding: '6px 0', borderTop: '1px solid var(--trello-border)' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</div>
              <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', fontFamily: 'monospace' }}>{p.perm_key}</div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ============ Create / Edit Role modal ============
const COLORS = ['#DE350B', '#0079BF', '#519839', '#D29034', '#89609E', '#00AECC', '#7C3AED', '#EB5A46'];

function RoleModal({ role, grouped, onClose, onSaved }) {
  const isNew = !role;
  const [form, setForm] = useState({
    name: role?.name || '',
    description: role?.description || '',
    color: role?.color || COLORS[0],
    is_active: role ? !!role.is_active : true,
  });
  const [perms, setPerms] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (role) {
      api.get(`/roles/${role.id}`).then(({ data }) => setPerms(new Set(data.permissions)));
    }
  }, [role]);

  const togglePerm = (k) => {
    const s = new Set(perms);
    if (s.has(k)) s.delete(k); else s.add(k);
    setPerms(s);
  };
  const toggleCategory = (cat) => {
    const s = new Set(perms);
    const keys = grouped[cat].map((p) => p.perm_key);
    const allOn = keys.every((k) => s.has(k));
    for (const k of keys) { if (allOn) s.delete(k); else s.add(k); }
    setPerms(s);
  };

  const save = async () => {
    if (!form.name.trim()) return alert('Name required');
    setBusy(true);
    try {
      let id = role?.id;
      if (isNew) {
        const { data } = await api.post('/roles', { ...form, permissions: [...perms] });
        id = data.role_id;
      } else {
        await api.patch(`/roles/${role.id}`, form);
        await api.put(`/roles/${role.id}/permissions`, { permissions: [...perms] });
      }
      onSaved(id);
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  const categories = Object.keys(grouped);

  return (
    <Portal>
    <div className="tm-modal-overlay" style={{ zIndex: 9999 }} onClick={onClose}>
      <div className="tm-modal" style={{ maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()} data-testid="role-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{isNew ? 'Create role' : `Edit role — ${role.name}`}</h2>
          <button className="tm-btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="tm-label">Name</div>
            <input className="tm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name"
              disabled={role?.is_system} />
            {role?.is_system && <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 4 }}>System role — name cannot be changed</div>}
          </div>
          <div>
            <div className="tm-label">Description</div>
            <input className="tm-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="role-desc" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="tm-label">Color</div>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setForm({ ...form, color: c })}
                  style={{ width: 28, height: 28, borderRadius: 6, background: c, border: form.color === c ? '3px solid var(--trello-text)' : '2px solid #fff', cursor: 'pointer' }}
                  data-testid={`role-color-${c}`} />
              ))}
            </div>
          </div>
          <div>
            <div className="tm-label">Status</div>
            <label className="flex items-center gap-2 mt-2 cursor-pointer" style={{ fontSize: 14, fontWeight: 600, color: 'var(--trello-text)' }}>
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="role-active" />
              Active (Enabled)
            </label>
          </div>
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="tm-label" style={{ marginBottom: 0 }}>Permissions ({perms.size} selected)</div>
          <input className="tm-input" placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 200 }} data-testid="role-perm-search" />
        </div>
        <div style={{ overflowY: 'auto', flex: 1, border: '1px solid var(--trello-border)', borderRadius: 6, padding: 8, background: 'var(--trello-card-bg)' }}>
          {categories.map((cat) => {
            const list = grouped[cat].filter((p) => !q.trim() || p.label.toLowerCase().includes(q.toLowerCase()) || p.perm_key.includes(q.toLowerCase()));
            if (!list.length) return null;
            const allOn = list.every((p) => perms.has(p.perm_key));
            const someOn = list.some((p) => perms.has(p.perm_key));
            return (
              <div key={cat} className="mb-2">
                <label className="flex items-center gap-2 p-1 cursor-pointer" style={{ fontWeight: 700, fontSize: 12, color: 'var(--trello-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }} onClick={() => toggleCategory(cat)}>
                  <input type="checkbox" checked={allOn} ref={(el) => el && (el.indeterminate = someOn && !allOn)} readOnly data-testid={`cat-${cat}`} />
                  {cat} <span style={{ color: 'var(--trello-text-muted)', fontWeight: 400 }}>({list.filter((p) => perms.has(p.perm_key)).length}/{list.length})</span>
                </label>
                <div className="pl-6">
                  {list.map((p) => (
                    <label key={p.perm_key} className="flex items-start gap-2 p-1 cursor-pointer hover:bg-slate-50 rounded" data-testid={`perm-${p.perm_key}`}>
                      <input type="checkbox" checked={perms.has(p.perm_key)} onChange={() => togglePerm(p.perm_key)} style={{ marginTop: 3 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{p.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', fontFamily: 'monospace' }}>{p.perm_key}</div>
                        {p.description && <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{p.description}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 mt-3">
          <button className="tm-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="tm-btn" onClick={save} disabled={busy} data-testid="save-role">
            <Save size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />
            {busy ? 'Saving…' : (isNew ? 'Create role' : 'Save changes')}
          </button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
